import { useEffect, useContext, useState, useMemo } from 'react';
import { DetectionOverview, Table, Faq, HubspotModal, UserContext, NotificationContext } from '@mdc/ui';
import { Row, Col } from 'react-bootstrap';
import { i18nUtility, InternalNavigation, localStorage, logService, QueryParams, CDNService, SERVICE_ERRORS  } from '@mdc/services';
import './PackageEfficacy.scss';
import PropTypes from 'prop-types';
import { NOTIFICATION_TYPES, ROUTES, HUBSPOT_PORTAL_ID, HUBSPOT_FORMS } from '@mdc/constants';
import { useTranslation } from 'react-i18next';

import Img from 'gatsby-image';
import Helmet from 'react-helmet';
import { OutboundLink } from 'gatsby-plugin-google-analytics';

const RESTRICTED_TABLE_ROWS = 5;
const TABLE_ROWS = 10;
const SUBMITTED_EMAIL = 'submittedEmail';

const PackageEfficacy = ({ faqs, engineImages } ) => {
    const [chartData, setChartData] = useState([]);
    const [threadData, setThreadData] = useState([]);
    const { notify } = useContext(NotificationContext);
    const [chartLabels, setChartLabels] = useState([]);
    const [chartPoints, setChartPoints] = useState([]);
    const [chartEngines, setChartEngines] = useState([]);
    const userContext = useContext(UserContext);
    const authIsLoading = userContext.state === userContext.STATES.PENDING;
    const [initialTablePage, setInitialTablePage] = useState(1);
    const [showHubspotModal, setShowHubspotModal] = useState(false);
    const handleClose = () => setShowHubspotModal(false);
    const { page } = QueryParams();
    const { t, ready } = useTranslation();
    const expectedResults = ['M4', 'M8', 'M12', 'M16', 'M20', 'MMAX', 'MCL'];
    const detectionColumns = [
        {
            Header: t('MetaDefender Core Package'),
            accessor: 'package'
        },
        {
            Header: t('4 Engines'),
            accessor: 'M4'
        },
        {
            Header: t('8 Engines'),
            accessor: 'M8'
        },
        {
            Header: t('12 Engines'),
            accessor: 'M12',
        },
        {
            Header: t('16 Engines'),
            accessor: 'M16'
        },
        {
            Header: t('20 Engines'),
            accessor: 'M20'
        },
        {
            Header: t('Max Engines'),
            accessor: 'MMAX'
        },
        {
            Header: t('Cloud *'),
            accessor: 'MCL'
        },
    ];

    const threadColumns = [
        {
            Header: t('Detected Threat'),
            accessor: 'threat_name'
        },
        {
            Header: t('4 Engines'),
            accessor: 'M4'
        },
        {
            Header: t('8 Engines'),
            accessor: 'M8'
        },
        {
            Header: t('12 Engines'),
            accessor: 'M12',
        },
        {
            Header: t('16 Engines'),
            accessor: 'M16'
        },
        {
            Header: t('20 Engines'),
            accessor: 'M20'
        },
        {
            Header: t('Max Engines'),
            accessor: 'MMAX'
        },
        {
            Header: t('Cloud'),
            accessor: 'MCL'
        },
    ];

    const language = i18nUtility.lookupLang().replace(/-/g, '_');
    const form = HUBSPOT_FORMS[language];

    const handleSubmit = () => {
        localStorage.setItem(SUBMITTED_EMAIL, true);
    };

    const emailSubmitted = localStorage.getItem(SUBMITTED_EMAIL);

    const restrictTable = useMemo(() => {
        if (authIsLoading) {
            return null;
        } else if (userContext.state === userContext.STATES.LOGGED_OUT ) {
            if (emailSubmitted === true){
                return TABLE_ROWS;
            }
            return RESTRICTED_TABLE_ROWS;
        }
    }, [emailSubmitted, authIsLoading]);

    const onCDNerror = (data) => {
        logService.error(data); // eslint-disable-line no-console
        notify({
            message: t('Report not available. Viewing the latest report'),
            type: NOTIFICATION_TYPES.CRITICAL
        });
    };

    const onCDNinvalid = () => {
        notify({
            message: t('There was an issue loading the report. Please try again later'),
            type: NOTIFICATION_TYPES.CRITICAL
        });
    };

    const engineList = useMemo(() =>  {
        const engineRecord = [];
        if (!chartEngines) {
            return;
        }

        Object.keys(chartEngines).forEach((item) =>  {
            if (item && expectedResults.includes(item)){
                const images = [];
                chartEngines[item].forEach((el, index) => {
                    el = el.replace(/!|\s/g, '').toLowerCase();
                    const fluidImage = engineImages[el] ? engineImages[el].fluid : null;
                    if (fluidImage) {
                        images.push( <span className="graphImg" key={index}> <Img fluid={fluidImage} alt={el}/> </span>);
                    }
                });
                engineRecord[item] = images;
            }
        });
        return engineRecord;

    }, [engineImages, chartEngines]);

    const handleCDNnavigate = (path) => {
        InternalNavigation.navigation(path);
    };

    useEffect(() => {
        (async () => {
            try {
                CDNService.on(SERVICE_ERRORS.REPORT_ERROR, onCDNerror);
                CDNService.on(SERVICE_ERRORS.INVALID_REPORT, onCDNinvalid);
                const latest = await CDNService.getCDNReport('package-efficacy-report-latest-v2.json', 'reports/', handleCDNnavigate);
                const emptyLabels = [];
                const emptyPoints = [];
                const emptyEngines = {};

                if (latest && latest.avlist && latest.top_search && latest.top_search.data){
                    const detectionResponse = latest.avlist.reduce((data, item) => {
                        const name = item?.name?.toUpperCase?.();
                        if (name && expectedResults.includes(name)){
                            emptyLabels.push(name);
                            emptyPoints.push(parseFloat(item.details.result));

                        }
                        emptyEngines[name] = item.details.engines;
                        data[name] = item.details.result;
                        return data;
                    }, { 'package': t('Detection of top 10000 threats') });

                    setChartData([detectionResponse]);
                    setChartLabels(emptyLabels);
                    setChartPoints(emptyPoints);
                    setChartEngines(emptyEngines);

                    const threadResponse = [];
                    latest.top_search.data.forEach((el) => {
                        el.forEach((thread) => {
                            const infectedHASH = thread.data_id ? `${ROUTES.results.href}/file/${thread.data_id}/regular/overview` :`${ROUTES.results.href}/file/${thread.sha1}/hash/overview`;
                            const scanResults = thread.scan_results;

                            const threadKeys = Object.keys(scanResults).reduce((data, key) => {

                                if (key === 'threat_name'){
                                    data[key] = <OutboundLink href={i18nUtility.rebuildUrlWithLangPrefix(infectedHASH)}> {scanResults[key]} </OutboundLink>;
                                    return data;
                                }
                                const packageName = key.toUpperCase();
                                data[packageName] = scanResults[key].scan_result_i ? <i className="icon-circle detectedIcon"/> : <i className="icon-undetected-threat notDetectedIcon"/>;

                                return data;
                            }, {});
                            threadResponse.push(threadKeys);
                        });
                    });

                    setThreadData(threadResponse);

                }
            } catch (err)  {
                logService.error(err); // eslint-disable-line no-console
            }
        })();
    }, []);

    useEffect(() => {
        if (!page || Number.isNaN(page) ||  page > 10  || page < 1 || !Number.isInteger(Number(page)) ) {
            return;
        }
        if (restrictTable && page > restrictTable) {
            setInitialTablePage(restrictTable);
            tablePage(restrictTable);
            setShowHubspotModal(true);
        } else {
            setInitialTablePage(+page);
        }

    }, [page, threadData, authIsLoading]);

    const tableLegend = <p className="legend" >{t('Most searched threats on MetaDefender Cloud over the past 30 days')}</p>;

    const tablePage = (pageNumber) => {
        window && window.history.replaceState('', '', `?page=${pageNumber}`);
    };

    if (authIsLoading || !ready) {
        return null;
    }

    return (
        <>
            <Helmet>
                <script
                    src="https://code.jquery.com/jquery-2.2.4.min.js"
                    integrity="sha256-BbhdlvQf/xTY9gja0Dq3HiwQF8LaCRTXxZKRutelT44="
                    crossOrigin="anonymous"/>
            </Helmet>

            <div className="packageEfficacy">
                <Row>
                    <Col md={6} xs={12}>
                        <h1>
                            {t('Package Efficacy Report')}
                        </h1>
                        <p className="topPageText">
                            {t('We compile the most searched-for threats from the MetaDefender Cloud database over the past month, and you can see them below along with data about how well they are detected by our MetaDefender Core Package')}
                        </p>
                    </Col>
                    <Col md={6} xs={12} >
                        <div className="multiscan">
                            <i className="icon icon-multiscan"/>
                            <div className="content">
                                <span className="metascanText">{t('metascan')}</span>
                                <span>{t('Multiscanning')}</span>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <h2 className="title detection">{t('Detection Efficacy Overview')}</h2>
                    </Col>
                </Row>

                <Row>
                    <Col >
                        <div className="scrollDiv">
                            <DetectionOverview columns={detectionColumns} data={chartData} labels={chartLabels} points={chartPoints} engines={engineList}/>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <h2 className="title">{t('Threat Detection Matrix')}</h2>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6}>
                        {t('Please note that the detection data comes from Software Development Kit (SDK) and Command Line Interface (CLI) package versions of these anti-malware engines, using static analysis only, and not from endpoint desktop applications which may be capable of enhanced behavioral and other dynamic analysis, so detection results may differ significantly from commercial endpoint performance. The data below should not be used for comparing the performance of desktop or server anti-malware applications')}
                    </Col>
                    <Col sm={12} md={6} className="threatText">
                        {t('To emphasize the value of multi-scanning, we wanted to show how the OPSWAT MetaDefender Core packages can detect the top threats on our list. As more scan engines are added, more of the top threats are detected, indicating the value added with each MetaDefender Core Packages increment. 100% detection is considered to be the free version of MetaDefender Cloud because it contains the biggest number of engines. Although this package is not available commercially, we use it for demo and analytics purposes. Optimizations are incorporated in our algorithms to avoid deviations by false positives')}
                    </Col>
                </Row>
                <div className="tableSection">
                    <Table
                        columns={threadColumns}
                        data={threadData}
                        useSorting={false}
                        footerInfoBox={tableLegend}
                        rowsPerPage={10}
                        onPageChange={tablePage}
                        onPageChangeRequest={(currentPage) => currentPage > restrictTable && setShowHubspotModal(true)}
                        initialPage={initialTablePage}
                        restrictTable={restrictTable}
                    />
                </div>

                <Col>
                    <div className="faqsSection">
                        <Faq faqs={faqs(t)} faqTitle={t('FAQs')}></Faq>
                    </div>
                </Col>

                <HubspotModal
                    portalId={HUBSPOT_PORTAL_ID}
                    formId={form.FORM_ID}
                    show={showHubspotModal}
                    handleClose={handleClose}
                    handleSubmit={handleSubmit}
                    title={t('Detection rate of top 10,000 threats')}
                />

            </div>
        </>
    );
};

PackageEfficacy.propTypes = {
    faqs: PropTypes.func,
    data: PropTypes.object,
    engineImages: PropTypes.object
};

PackageEfficacy.defaultProps = {
    faqs: []
};

export default PackageEfficacy;


